import {GetJournalEntry} from "../../Server/DB/journalEntries.js";
import {CreateAccessor} from "mobx-graphlink";
import {O} from "web-vcore";
import {JournalSegment, JournalSegment_HideableField} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {JournalImporterState} from "./journalImporter.js";

export enum JournalDisplayMode {
	entryList = "entryList",
	entryGrid = "entryGrid",
	dreamList = "dreamList",
	sceneList = "sceneList",
	/** backport of the Discord dream logs into `JournalSegment.publicText` */
	importer = "importer",
}

export enum JournalSort {
	date = "date",
	longText_length = "longText_length",
}

export enum ClarityUI {
	percent = "percent",
	stars = "stars",
}
export enum MappingProgressUI {
	percent = "percent",
	stars = "stars",
}
export enum TechExecutionUI {
	percent = "percent",
	stars = "stars",
}

export enum SummarizeTab {
	simple = "simple",
	llmTemplate = "llmTemplate",
	llmGenerate = "llmGenerate",
}

export enum CleanupTab {
	llmTemplate = "llmTemplate",
	llmGenerate = "llmGenerate",
}

/** Which of a dream-segment's three text fields the segment UI is currently showing. Only one is
 * displayed at a time, because stacking all three made the segment panel unwieldy. */
export enum SegmentTextTab {
	shortText = "shortText",
	longText = "longText",
	publicText = "publicText",
}

export class JournalState {
	//@O selectedUser: string;
	@O accessor displayMode = JournalDisplayMode.entryList;

	/** state of the "Importer" tab (Discord dream-log backport into `publicText`) */
	@O accessor importer = new JournalImporterState();

	@O accessor selectedJournalEntry: string|n;
	@O accessor clarityUI = ClarityUI.stars;
	@O accessor mappingProgressUI = MappingProgressUI.stars;
	@O accessor techExecutionUI = TechExecutionUI.stars;
	@O accessor summarize_tab = SummarizeTab.llmGenerate;
	@O accessor summarize_llmTemplate = llmTemplateDefault_summarize;
	@O accessor cleanup_tab = CleanupTab.llmGenerate;
	@O accessor cleanup_llmTemplate = llmTemplateDefault_cleanup;

	/** Which text field the segment UI shows (short / long / public). Defaults to long-text, since
	 * that is the one worked on most often. */
	@O accessor segmentTextTab = SegmentTextTab.longText;

	// entry-list + dream-list
	// ==========

	@O accessor sortBy = JournalSort.date;
	@O accessor sort_desc = true;
	// these affect the entry-list previews directly, and the dream-list indirectly (via the hover-panel for dreams)
	@O accessor textPreviewMultiline = false;
	@O accessor longTextLengthLimit_enabled = false;
	@O accessor longTextLengthLimit_chars = 1000;

	// entry-list (only)
	@O accessor limit = 10;
	@O accessor filter = new JournalFilterState();

	// dream-list (only)
	@O accessor dreamList_onlyLucids = false;
	@O accessor dreamList_onlyIntegratable = false;
	@O accessor dreamList_sidePanel_show = true;
	@O accessor dreamList_sidePanel_size = .3; // 0.3 = 30% of the screen width
	@O accessor dreamList_selectedDream: string|n;

	// scene-list (only)
	@O accessor sceneList_onlyLucids = false;
	@O accessor sceneList_onlyIntegratable = false;
	@O accessor sceneList_sidePanel_show = true;
	@O accessor sceneList_sidePanel_size = .3; // 0.3 = 30% of the screen width
	@O accessor sceneList_selectedScene: string|n;

	// entry-grid
	// ==========

	@O accessor entriesPerGridCell = 3;

	// entry-ui
	// ==========

	@O accessor segmentFieldsToHide = [] as Array<JournalSegment_HideableField>;
	@O accessor segmentFields_showAll = false;
}
export const llmTemplateDefault_summarize = `
Please create a VERY compact summary of the following dream.

Rules:
* Be very compact. (grammar rules can be skipped)
* Separate events using semicolons (;).
* The entire summary should be lowercase, in chronological order, and in the present tense.
* Don't talk about your making a summary, just return the summary itself.
* For dreamer's actions, say "i" or "me" instead of "dreamer"; and actually, omit "i"/"me" unless it's needed for disambiguation.

Dream:
\`\`\`
$dream
\`\`\`
`.trim();
export const llmTemplateDefault_cleanup = `
Please clean up the text of the following dream (which used imperfect speech-to-text transcription).

Objectives:
* Fix punctuation.
* Ensure complete sentences. (remove or replace punctuation marks to do so)
* Add paragraph breaks.
* Suggest fixes for possibly mis-transcribed words.

Rules:
* Preserve all words from the original text. (this includes timestamps!)
* If you think a word was mis-transcribed, KEEP THE ORIGINAL, and simply ADD your best guess in brackets: like dis [this?]
* Avoid saying "Here is the cleaned up text" or similar; just return the cleaned-up text on its own.

Dream:
\`\`\`
$dream
\`\`\`
`.trim();

export class JournalFilterState {
	@O accessor showOnlyLucids = false;
	// text_include is an entry-level AND segment-level filter
	@O accessor text_include = "";
	@O accessor filterWithinJournalEntry = false;

	// segment-level filters
	@O accessor showOnlyIntegratable = false;
	@O accessor dreamEvents_enabled = false;
	@O accessor dreamEvents_min = -1;
	@O accessor dreamEvents_max = -1;
	@O accessor words_enabled = false;
	@O accessor words_min = -1;
	@O accessor words_max = -1;
}

export const GetSelectedJournalEntryID = CreateAccessor({ctx: 1}, function() {
	return this.store.main.journal.selectedJournalEntry;
});
export const GetSelectedJournalEntry = CreateAccessor(()=>{
	const selectedID = GetSelectedJournalEntryID();
	return GetJournalEntry(selectedID);
});